import { createContext, useContext, useEffect, useState, useRef } from "react";
import { useSelector } from "react-redux";
import { Socket } from "socket.io-client";

import { useParams } from "react-router-dom";

import { createAuthedSocket } from "@/utils/createAuthedSocket";

import { authSelector } from "@/pages/Public/redux/auth.slice";

interface SocketContextType {
	socket: Socket | null;
	actualData: SocketColumnUpdate[];
	clearActualData: () => void;
}

export interface SocketColumnUpdate {
	listId: number;
	listPersonId: number;
	customColumnId: number;
	value: string;
	status: string;
}

export enum ListPersonSocketAction {
	JOIN_ROOM = "join-room",
	CHANGE_STATUS = "change-status",
	ACTUAL_DATA = "actual-data",
	ERROR = "error",
}

const ListPersonSocketContext = createContext<SocketContextType | undefined>(undefined);

export const ListPersonSocketProvider = ({ children }: { children: React.ReactNode }) => {
	const [socket, setSocket] = useState<Socket | null>(null);
	const [actualData, setActualData] = useState<SocketColumnUpdate[]>([]);
	const auth = useSelector(authSelector);
	const token = auth?.token;
	const tokenRef = useRef(token);

	tokenRef.current = token;
	const { id } = useParams();

	const clearActualData = () => {
		setActualData([]);
	};

	useEffect(() => {
		if (!token || !id) {
			return;
		}

		const newSocket = createAuthedSocket(import.meta.env.VITE_API_URL, () => tokenRef.current);

		let hasLoggedError = false;

		newSocket.on("connect", () => {
			newSocket.emit(ListPersonSocketAction.JOIN_ROOM, id);
			hasLoggedError = false;
			console.info("Socket connected");
		});

		newSocket.on("connect_error", (error) => {
			if (!hasLoggedError) {
				console.info("Socket connection error:", error);
				hasLoggedError = true;
			}
		});

		newSocket.on(ListPersonSocketAction.ACTUAL_DATA, (data: SocketColumnUpdate) => {
			const alreadyExists = actualData.find(
				(item) =>
					item.listPersonId === data.listPersonId && item.customColumnId === data.customColumnId
			);

			if (alreadyExists) {
				// console.log("alreadyExists", alreadyExists, data);

				const newActualData = actualData.map((item) =>
					item.listPersonId === data.listPersonId && item.customColumnId === data.customColumnId
						? data
						: item
				);

				setActualData(newActualData);
			} else {
				setActualData((prev) => [...prev, data]);
			}
		});

		newSocket.on("disconnect", (reason) => {
			console.warn("Socket disconnected:", reason);
		});

		setSocket(newSocket);

		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		newSocket.on(ListPersonSocketAction.ACTUAL_DATA, (data: any) => {
			// console.log("ACTUAL_DATA", data);
		});

		return () => {
			newSocket.disconnect();
		};
	}, [token, id]);

	return (
		<ListPersonSocketContext.Provider
			value={{
				socket,
				actualData,
				clearActualData,
			}}
		>
			{children}
		</ListPersonSocketContext.Provider>
	);
};

export const useListPersonSocket = () => {
	const context = useContext(ListPersonSocketContext);

	if (!context) {
		throw new Error("useSocket must be used within a SocketProvider");
	}

	return context;
};
